import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, Divider } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { fetchMembersByMemberCategory, deleteMember } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

const memberClassLabel = (cls, t) => {
  switch (cls) {
    case "Collaborator": return t("member.class.Collaborator");
    case "Specialist": return t("member.class.Specialist");
    case "Investor": return t("member.class.Investor");
    case "ProductUser": return t("member.class.ProductUser");
    case "CompanyMember": return t("member.class.CompanyMember");
    default: return cls || "";
  }
};

const memberStateLabel = (state, t) => {
  switch (state) {
    case "Registered": return t("member.state.Registered");
    case "Assigned": return t("member.state.Assigned");
    case "Released": return t("member.state.Released");
    default: return state || "";
  }
};

export default function MemberCategoryMemberDetailPage() {
  const { businessDomainId, categoryId, memberId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const basePath = `/strategy/${businessDomainId}/member-categories/${categoryId}`;

  const loadData = useCallback(async () => {
    try {
      const members = await fetchMembersByMemberCategory(categoryId);
      const found = (Array.isArray(members) ? members : []).find(
        (m) => String(m.memberId) === String(memberId)
      );
      setData(found || null);
    } catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, memberId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    try {
      await deleteMember(memberId);
      notify.success(t("message.delete.success"));
      navigate(`${basePath}/members`, { replace: true });
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  if (!data) return null;

  const fullName = `${data.lastName || ""} ${data.firstName || ""}`.trim();

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("member.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />}
            onClick={() => navigate(`${basePath}/members/${memberId}/edit`)}>
            {t("common.edit")}
          </Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}>
            {t("common.delete")}
          </Button>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12} md={6}>
            <DetailField label={t("member.lastName")} value={data.lastName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("member.firstName")} value={data.firstName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("member.email")} value={data.email} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("member.memberNumber")} value={data.memberNumber} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("member.class")} value={memberClassLabel(data.memberClass, t)} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("member.state")} value={memberStateLabel(data.memberState, t)} />
          </Grid>
          {data.memberDescription && (
            <Grid size={12}>
              <DetailField label={t("common.description")} value={data.memberDescription} />
            </Grid>
          )}
        </Grid>
      </DetailSection>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/member-categories/${categoryId}/members`)}>{t("common.back")}</Button>
      </Box>

      <ConfirmDialog open={confirmOpen} onConfirm={handleDelete} onCancel={() => setConfirmOpen(false)} />
    </Box>
  );
}
